// src/features/system/edge-tasks/components/EdgeTasksPanel.tsx
import React, { useMemo, useState } from 'react';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import { timeAgo } from '@/utils/date';
import { useEdgeTasks } from '../hooks/useEdgeTasks';
import type { EdgeTask } from '../api/edgeTasksApi';

// ── Status badge ────────────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  pending:   'bg-yellow-500/20 text-yellow-300',
  claimed:   'bg-blue-500/20 text-blue-300',
  completed: 'bg-emerald-500/20 text-emerald-300',
  failed:    'bg-red-500/20 text-red-300',
};

function StatusBadge({ status, label }: { status: string; label: string }) {
  const cls = STATUS_COLORS[status] ?? 'bg-neutral-500/20 text-neutral-300';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

// ── Dispatch form ───────────────────────────────────────────────────────
const WORKFLOW_PRESETS = [
  'edge.screenshot',
  'edge.voice_capture',
  'edge.system_metrics',
] as const;

function DispatchForm({ onDispatch, isPending }: {
  onDispatch: (wk: string, agentId: string, inputs: string) => void;
  isPending: boolean;
}) {
  const [wk, setWk] = useState(WORKFLOW_PRESETS[0] as string);
  const [agentId, setAgentId] = useState('');
  const [inputs, setInputs] = useState('{}');

  return (
    <div className="pac-glass rounded-lg p-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Workflow key
        <select
          value={wk}
          onChange={(e) => setWk(e.target.value)}
          className="bg-neutral-800/70 border border-neutral-700 rounded px-2 py-1 text-sm text-neutral-100"
        >
          {WORKFLOW_PRESETS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Agent ID <span className="text-neutral-600">(optional)</span>
        <input
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          placeholder="any"
          className="bg-neutral-800/70 border border-neutral-700 rounded px-2 py-1 text-sm text-neutral-100 w-40"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Inputs (JSON)
        <input
          value={inputs}
          onChange={(e) => setInputs(e.target.value)}
          className="bg-neutral-800/70 border border-neutral-700 rounded px-2 py-1 text-sm text-neutral-100 w-48"
        />
      </label>

      <button
        disabled={isPending}
        onClick={() => onDispatch(wk, agentId, inputs)}
        className="px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-sm font-medium text-white disabled:opacity-40 transition-colors"
      >
        {isPending ? 'Dispatching…' : 'Dispatch'}
      </button>
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────

export default function EdgeTasksPanel() {
  const { data, error, dispatch } = useEdgeTasks();

  const items = useMemo(() => data?.items ?? [], [data]);

  const columns: ColumnDef<EdgeTask>[] = useMemo(() => [
    {
      header: 'ID',
      accessor: 'id' as keyof EdgeTask,
      Cell: (val: number) => <span className="font-mono text-xs">{val}</span>,
    },
    {
      header: 'Workflow',
      accessor: 'workflowKey' as keyof EdgeTask,
      Cell: (val: string) => <span className="font-mono text-xs">{val}</span>,
    },
    {
      header: 'Status',
      accessor: 'status' as keyof EdgeTask,
      Cell: (_val: string, row?: EdgeTask) => (
        <StatusBadge status={row?.status ?? ''} label={row?.statusLabel ?? row?.status ?? ''} />
      ),
    },
    {
      header: 'Agent',
      accessor: 'claimedBy' as keyof EdgeTask,
      Cell: (val: string | null, row?: EdgeTask) => (
        <span className="text-xs">{val ?? row?.agentId ?? '—'}</span>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt' as keyof EdgeTask,
      Cell: (val: string) => (
        <span className="text-xs text-neutral-400" title={val}>
          {timeAgo(val)}
        </span>
      ),
    },
    {
      header: 'Duration',
      accessor: 'durationMs' as keyof EdgeTask,
      Cell: (val: number | null) =>
        val != null ? (
          <span className="text-xs">{(val / 1000).toFixed(1)}s</span>
        ) : (
          <span className="text-xs text-neutral-600">—</span>
        ),
    },
    {
      header: 'Error',
      accessor: 'errorMessage' as keyof EdgeTask,
      Cell: (val: string | null) =>
        val ? (
          <span className="text-xs text-red-400 truncate max-w-[200px] inline-block" title={val}>
            {val}
          </span>
        ) : null,
    },
  ], []);

  function handleDispatch(wk: string, agentId: string, inputsRaw: string) {
    let inputs: Record<string, unknown> = {};
    try { inputs = JSON.parse(inputsRaw); } catch { /* keep empty */ }
    dispatch.mutate({
      workflow_key: wk,
      ...(agentId ? { agent_id: agentId } : {}),
      inputs,
    });
  }

  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      <DispatchForm onDispatch={handleDispatch} isPending={dispatch.isPending} />

      {error && (
        <div className="text-sm text-red-400 px-2">Failed to load edge tasks: {error.message}</div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        <MinimalisticTable<EdgeTask>
          columns={columns}
          data={items}
          noDataMessage="No edge tasks dispatched yet."
        />
      </div>

      {data && (
        <div className="text-[10px] text-neutral-600 px-1 shrink-0">
          {data.total} task{data.total !== 1 ? 's' : ''} — auto-refreshing every 15 s
        </div>
      )}
    </div>
  );
}
